import { IController } from 'angular';
import * as _ from 'lodash';
import * as moment from 'moment';

import { IRegentsExam, RegentsExam } from '../../../../ng2/shared/constants/regents.constant';
import { ISchool } from '../../../../ng2/shared/typings/interfaces/school.interface';
import { IStudent } from '../../../../ng2/shared/typings/interfaces/student.interface';
import { ISupportCategory, SupportCategories } from './../../../../ng2/shared/constants/support-categories.constant';
import { SdcActionsService } from './../../../sdc/flux/sdc-actions.service';
import { SupportScheduleService } from './../../services/support-schedule/support-schedule.service';

interface ISupportDetails {
  _id: string;
  category: string;
  description: string;
  date_time: string;
  lead: string;
  schedule: any;
}

export class StudentSupportModalCtrl implements IController {
  static inject: string[] = [
    // locals passed via service
    'mode',
    'exam',
    'studentIds',
    // resolved before open:
    'student',
    'studentSupport', // template from service
    'school',
    'supports',
    'v3View',
    // other dependencies
    '$mdDialog',
    'ErrorModal',
    'sdcActions',
    'BackgroundJobSpinner',
    'Support',
    'SupportScheduleService',
  ];

  public studentName: string;
  public activeSupports; // supports[]
  public filteredSupports; // supports[]
  public selectedCategory: ISupportCategory;
  public selectedSubOption: string;
  public selectedSupport: ISupportDetails;
  public supportsForExam: string[];
  public supportStartDate: Date;
  public supportReviewDate: Date;
  public calendarStartDate: Date;
  public calendarEndDate: Date;
  public examHumanReadable: string;
  public examsBySubject: string[];
  public subjectByExam: string;
  public supportCategories;
  public regentsExam: IRegentsExam;
  public studentSupportStatus;

  constructor(
    public mode,
    public exam: string,
    private studentIds: string[],
    public student: IStudent,
    public studentSupport,
    public school: ISchool,
    public supports,
    public v3View: boolean,
    private $mdDialog,
    private ErrorModal,
    private sdcActions: SdcActionsService,
    private BackgroundJobSpinner,
    private Support,
    private SupportScheduleService: SupportScheduleService,
  ) {
    this.onInit();
  }

  onInit(): void {
    // indicates whether this is bulk or single assignment
    this.mode = this.mode.UI;
    // ui name: decided by modal mode
    this.studentName =
      this.mode === 'ASSIGN_MANY' ? `${this.studentIds.length} students` : this.getDisplayName(this.student);
    // get active supports that will be further filtered by cat/suboption
    this.activeSupports = _.filter(this.supports, (support: any) => !support.isDeleted() && !support.isCompleted());
    // supportCategories with active supports
    this.supportCategories = this.getSupportCategoriesWithSupports(SupportCategories);
    // load basic studentData on init()
    this.studentSupport = this.appendStudentDataToStudentSupport(this.studentSupport);
    // if exam is passed to service, open modal prepopulated
    if (this.exam) this.prepopulateModalBasedOnExamName();
    // manage data inconsistencies
    this.manageModelToModalInconsistencies();
  }

  manageModelToModalInconsistencies(): void {
    // checkbox: string to boolean translation
    this.studentSupportStatus = { isActive: this.studentSupport.status === 'ACTIVE' ? false : true };
    // inconsistancies with schema and md-date-picker: one is isoString other needs date object
    const momentInstance: any = moment(new Date());
    const oneMonthFromStart = momentInstance.add(1, 'months');
    // maybe we want to change this name to studentSupportStartDate
    this.supportStartDate = this.studentSupport.startsOn ? new Date(this.studentSupport.startsOn) : new Date();
    this.supportReviewDate = !_.isEmpty(this.studentSupport.reviewOn)
      ? new Date(_.last(this.studentSupport.reviewOn))
      : new Date(oneMonthFromStart);
  }

  getSupportCategoriesWithSupports(categories): any {
    return _.reduce(
      categories,
      (result, value: ISupportCategory, key: string) => {
        if (_.find(this.activeSupports, { category: key })) value.hasActiveSupports = true;
        else value.hasActiveSupports = false;
        result[key] = value;
        return result;
      },
      {},
    );
  }

  // invoked on checkbox toggle
  checkSupportStatus(supportStatus: boolean): void {
    this.studentSupport.status = supportStatus === false ? 'ACTIVE' : 'COMPLETED';
  }

  // invoked when exam is passed in assignment mode -- ie: when opened from Regents Panel
  prepopulateModalBasedOnExamName(): void {
    this.regentsExam = _.find(RegentsExam, { key: this.exam }); // get necessary data when exam is passed in
    this.subjectByExam = this.regentsExam.subject.key;
    this.examHumanReadable = this.regentsExam ? this.regentsExam.humanName : null; // pull off humanName for view
    this.supportsForExam = this.getSupportsByExamName(this.exam);
    this.studentSupport.support.category = this.exam ? 'REGENTS_PREP' : null;
    this.getCategoryData(this.studentSupport.support.category);
    this.selectedSubOption = this.subjectByExam;
    this.getSupportsBySuboption(this.selectedCategory, this.subjectByExam);
  }

  // invoked when exam is passed in assignment mode
  getSupportsByExamName(examName: string): any[] {
    const {
      subject: { key },
    } = _.find(RegentsExam, { key: examName });
    return _.reduce(
      this.activeSupports,
      (result, { metaData, name }) => {
        // guard: !metaData or !examSubject on support
        if (!metaData || !metaData.examSubject) return result;
        if (metaData.examSubject === key) {
          result.push(name);
        }
        return result;
      },
      [],
    );
  }

  // invoked on open: takes studentSupport template, copies, appends and returns new student support
  appendStudentDataToStudentSupport<studentSupportTemplate>(studentSupportTemplate): studentSupportTemplate {
    const studentSupportCopy = angular.copy(studentSupportTemplate);
    studentSupportCopy.schoolId = this.school._id;
    studentSupportCopy.student.studentId = this.student._id;
    return studentSupportCopy;
  }

  // invoked when cat is selected: gets category object
  getCategoryData(categoryName: string): ISupportCategory | string[] {
    // clear modal: this is invoked on each ng-change of the selected support cat
    this.filteredSupports = null;
    this.selectedSubOption = null;
    this.selectedSupport = null;

    if (!categoryName) return;
    this.selectedCategory = SupportCategories[categoryName];
    // if there are no suboptions, just filter
    if (!this.selectedCategory.subOptions) {
      return this.getSupportsByCategory(this.selectedCategory);
    } else {
      this.selectedCategory.subOptions = this.getSuboptionsForSelectedCategory(this.selectedCategory);
      return this.selectedCategory;
    }
  }

  // appends hasActiveSupports field to suboptions obj for disable functionality in dropdown
  getSuboptionsForSelectedCategory(selectedCategory: ISupportCategory) {
    const { subOptions, metaData: metaDataField } = selectedCategory;
    return _.map(subOptions, option => {
      let hasSupports = false;
      _.each(this.activeSupports, (support: any) => {
        if (!support.metaData || !support.metaData[metaDataField]) return;
        if (support.metaData[metaDataField] === option.value) hasSupports = true;
      });
      option.hasActiveSupports = hasSupports;
      return option;
    });
  }

  getSupportsBySuboption(selectedCategory, subOption): string[] {
    const { metaData: metaDataField } = selectedCategory;
    this.selectedSupport = null;
    this.studentSupport.support.name = null;
    const filterBySubOption = () =>
      _.reduce(
        this.activeSupports,
        (supportsCollection, support: any) => {
          const { name } = support;
          if (!support.metaData || !support.metaData[metaDataField]) return supportsCollection;
          if (support.metaData[metaDataField] === subOption) supportsCollection.push(name);
          return supportsCollection;
        },
        [],
      );
    return (this.filteredSupports = filterBySubOption());
  }

  // get all supports when a category is selected
  // smart enough to know if it should filter on the subOptions or category itself
  getSupportsByCategory(category: ISupportCategory, subOption?): string[] {
    // clear modal: this is invoked on each ng-change of the support cat
    this.selectedSupport = null;
    this.studentSupport.support.name = null;

    const filterByCategory = () =>
      _.reduce(
        this.activeSupports,
        (supportsCollection, support: any) => {
          const { category, name } = support;
          if (category === this.studentSupport.support.category) supportsCollection.push(name);
          return supportsCollection;
        },
        [],
      );
    return (this.filteredSupports = filterByCategory());
  }

  // get modal body details based on selected support.
  // Invoked on user support selection & editMode init
  getSupportDetails(supportName): ISupportDetails {
    const support: any = _.find(this.supports, { name: supportName });
    this.updateStudentSupportStartAndReview(support, this.mode);
    return {
      _id: support._id,
      category: support.category,
      description: support.description,
      date_time: this.createDateTimeSummary(support),
      lead: support.activityLead,
      schedule: support.schedule,
    };
  }

  // function to update supportStartDate only if we are NOT in edit mode
  updateStudentSupportStartAndReview(support, mode): void {
    if (mode !== 'EDIT_SINGLE') {
      // startsOn
      this.supportStartDate = new Date(support.schedule.startsOn);
      this.studentSupport.startsOn = support.schedule.startsOn;
      // reviewsOn
      const momentInstance: any = moment(support.schedule.startsOn);
      const oneMonthFromStart = momentInstance.add(1, 'months');
      this.supportReviewDate = new Date(oneMonthFromStart);
      this.studentSupport.reviewOn = [oneMonthFromStart];
    }
    return;
  }

  // updates min date in datePicker:
  setEarliestAssignmentDateLimit(selectedSupport): void {
    const {
      schedule: { startsOn, endsOn },
    } = selectedSupport;
    this.calendarStartDate = new Date(startsOn);
    this.calendarEndDate = new Date(endsOn);
  }

  // set selected support details in modal body
  setSelectedSupportDetails<studentSupport>(studentSupport): studentSupport {
    this.selectedSupport = this.getSupportDetails(studentSupport.support.name);
    studentSupport.support.supportId = this.selectedSupport._id;
    this.setEarliestAssignmentDateLimit(this.selectedSupport);
    return studentSupport;
  }

  // Requests:
  assignStudentSupport(): void {
    this.studentSupport.$create().catch(error => this.ErrorModal.show('Error assigning support', error));
    this.$mdDialog.hide({
      action: 'ADD_STUDENT_SUPPORT',
      studentSupport: this.studentSupport,
    });
  }

  bulkAssignStudentSupport(): void {
    const selectedStudentSupportName = this.studentSupport.support.name;
    const support = _.find(this.activeSupports, { name: selectedStudentSupportName });
    const backgroundJob = this.sdcActions
      .createStudentSupportsAsync(this.studentIds, support)
      .catch(errorResponse => this.ErrorModal.show('Unable to save your changes', errorResponse));
    // close it down:
    this.$mdDialog.hide({
      action: 'BULK_ADD_STUDENT_SUPPORT',
      studentIds: this.studentIds,
    });
    this.BackgroundJobSpinner.activateSpinner('Assigning Students to Support', backgroundJob);
  }

  patchStudentSupport(): void {
    this.studentSupport
      .$patchFields(['startsOn', 'reviewOn', 'status'])
      .catch(error => this.ErrorModal.show('Error updating support', error));
    this.$mdDialog.hide({
      action: 'UPDATE_STUDENT_SUPPORT',
      studentSupport: this.studentSupport,
    });
  }

  cancelDialog(): void {
    this.$mdDialog.hide();
  }

  // HELPER FUNCTIONS:
  validateDate(studentSupport): boolean {
    const formatted = moment(this.studentSupport.startsOn).format('YYYY-MM-DD');
    const momentInstance: any = moment(formatted);
    const isBetween = this.SupportScheduleService.dateIsBetween(this.selectedSupport, momentInstance);
    return studentSupport.startsOn && _.last(studentSupport.reviewOn) && isBetween;
  }

  private createDateTimeSummary(support): string {
    const { endsOn, startsOn, startTime, endTime, repeats: interval } = support.schedule;
    return interval === 'NONE'
      ? `${startTime} to ${endTime}
      from ${moment(startsOn.replace(/T.+/, '')).format('MM-DD')}
      to ${moment(endsOn.replace(/T.+/, '')).format('MM-DD')}`
      : `${_.capitalize(interval.toLowerCase())} from ${startTime} to ${endTime}
      from ${moment(startsOn.replace(/T.+/, '')).format('MM-DD')}
      to ${moment(endsOn.replace(/T.+/, '')).format('MM-DD')}`;
  }

  private getDisplayName(student): string {
    const {
      studentDetails: {
        name: { last, first },
      },
    } = student;
    return `${last}, ${first}`;
  }
}
